import { useMemo } from 'react';

import {
  useRxDocuments,
  useRxDeleteById,
  useRxDocumentsLazy,
  useRxCreateMutation,
  useRxUpdateMutationById,
  useRxPatchMutation,
  useRXDeleteByIds,
  type RxQueryParams,
  type MutationOptions,
} from '@/hooks/rxdb-base';
import { useHeldValue } from '@/hooks/use-held-value';
import { useSearch, convertSortToString, type UseWithSearchParams } from '@/hooks/use-search';
import { useConditionalSearch, type ConditionalSearchResult } from '@/hooks/use-conditional-search';

import type { Block, Instruction, Note } from '@flintmint/core-shared/rxdb';

// create

export const useInstructionCreate = () => {
  const { mutate: createMutation, isLoading } = useRxCreateMutation<Instruction>('instructions');

  const createWithDefaults = async (payload: Partial<Instruction> = {}, mutationOptions?: MutationOptions) => {
    const instructionData = {
      name: '',
      content: '',
      tagIds: [],
      ...payload,
    };

    return createMutation(instructionData, mutationOptions);
  };

  return { mutate: createWithDefaults, isLoading };
};

// read

interface UseInstructionsOptions {
  keepPreviousWhileLoading?: boolean;
  listen?: boolean;
}

export const useInstructions = (queryParams?: RxQueryParams<Instruction>, options?: UseInstructionsOptions) => {
  const { keepPreviousWhileLoading = false, listen = true } = options || {};

  const { data = [], isLoading } = useRxDocuments<Instruction>('instructions', queryParams, {
    enabled: listen,
  });
  const held = useHeldValue(data, listen && !isLoading);

  if (!listen) {
    return {
      data: held,
      isLoading: false,
    };
  }

  return {
    data: keepPreviousWhileLoading && isLoading ? held : data,
    isLoading,
  };
};

export const useResolvedInstructionIds = (ids: string[]) => {
  // Soft-ref resolution with raw-key fallback while loading: see the canonical comment on
  // useResolvedUserSourceIds in use-user-sources.ts.
  const { data: resolvedInstructions, isLoading } = useInstructions({ ids });

  return useMemo(
    () => (isLoading ? ids : resolvedInstructions.map((instruction) => instruction.id)),
    [isLoading, ids, resolvedInstructions]
  );
};

export const useEnabledInstructions = (doc?: Note | Block) => {
  const enabledInstructionIds = getEnabledInstructionIds(doc);
  return useRxDocuments<Instruction>('instructions', { ids: enabledInstructionIds });
};

export const useEnabledInstructionsLazy = () => {
  const { query: queryDocuments, isLoading } = useRxDocumentsLazy<Instruction>('instructions');

  const query = async (doc?: Note | Block) => {
    const enabledInstructionIds = getEnabledInstructionIds(doc);
    if (enabledInstructionIds.length === 0) {
      return [];
    }
    return queryDocuments({ ids: enabledInstructionIds });
  };

  return { query, isLoading };
};

const getEnabledInstructionIds = (doc?: Note | Block) => {
  return Object.entries(doc?.instructions || {}).flatMap(([id, enabled]) => (enabled ? [id] : []));
};

export const useInstructionsWithSearch = ({
  searchTerm,
  tagIds,
  ids: instructionIds,
  sort,
  highlightMatches = false,
}: UseWithSearchParams<Instruction>): ConditionalSearchResult<Instruction> => {
  const isSearching = !!searchTerm;

  const searchQuery = {
    term: searchTerm,
    where: {
      tagIds,
      ...(instructionIds && { ids: instructionIds }),
      types: ['instructions'],
    },
    sort: convertSortToString(sort),
    highlightMatches,
  };
  const searchResults = useSearch(searchQuery, isSearching);

  const rxdbResults = useInstructions({ ids: instructionIds, tagIds, sort });

  return useConditionalSearch<Instruction>(searchResults, rxdbResults, isSearching);
};

export const useInstructionsLazy = () => useRxDocumentsLazy<Instruction>('instructions');

// update

export const useInstructionPatch = () => useRxPatchMutation<Instruction>();

// deprecated
export const useInstructionUpdateById = () => useRxUpdateMutationById<Instruction>('instructions');

// delete

export const useInstructionDeleteById = () => useRxDeleteById<Instruction>('instructions');

export const useInstructionsDeleteByIds = () => useRXDeleteByIds<Instruction>('instructions');
